import './reset.css';
import './app.css';
import '@aws-amplify/ui-react/styles.css';

import * as constants from 'app-constants';

import { FeatureFlagProvider, useIdentifyForFeatureFlags } from './feature-flags/main';
import React, { FC } from 'react';

import { Auth } from 'aws-amplify';
import { Authenticator } from '@aws-amplify/ui-react';
import { ClientQuery } from './components/client-query.component';
import { CognitoUserAmplify } from '@aws-amplify/ui';
import { ErrorBoundary } from './error-boundary';
import { MainView } from './main-view';
import ReactDom from 'react-dom';
import { RedirectView } from './components/redirect-view.component';
import { RootLayout } from './components/root-layout.component';
import { config } from './feature-flags/config';
import { shouldRedirect } from './helpers/decommission';
import { useAppController } from './controllers/app.controller';
import { version } from '../package.json';

Auth.configure(constants.cognito);
export interface AuthedAppProps {
  user: CognitoUserAmplify | undefined;
}

const AuthedApp: FC<AuthedAppProps> = ({ user }) => {
  const [state, dispatch] = useAppController();
  const [hasLoaded, setHasLoaded] = React.useState(false);
  useIdentifyForFeatureFlags(
    async () => {
      const session = await Auth.currentSession();
      const userClientId = session.getIdToken().payload['custom:client_id'];
      return { kind: 'client_id', key: userClientId, appVersion: version };
    },
    () => {
      setHasLoaded(true);
    },
  );
  if (!hasLoaded) {
    return null;
  }
  return (
    <>
      <ClientQuery
        clientId={getClientId(user)}
        onError={error => dispatch({ type: 'error', error })}
        dispatch={dispatch}
      />
      <MainView {...state} dispatch={dispatch} />
    </>
  );
};

const App: FC = () => {
  // Once the legacy portal is decommissioned (or when previewing via ?redirect-preview), redirect
  // every visitor to the Garner Admin Portal before rendering the authenticator or loading data.
  if (shouldRedirect()) {
    return <RedirectView />;
  }
  return (
    <FeatureFlagProvider config={config}>
      <Authenticator.Provider>
        <ErrorBoundary>
          <RootLayout>
            <Authenticator hideSignUp loginMechanisms={['email']} variation="modal">
              {({ user }) => <AuthedApp user={user} />}
            </Authenticator>
          </RootLayout>
        </ErrorBoundary>
      </Authenticator.Provider>
    </FeatureFlagProvider>
  );
};

ReactDom.render(<App />, document.getElementById('approot'));

function getClientId(user: CognitoUserAmplify | undefined) {
  const strVal = user?.attributes?.['custom:client_id'];
  if (!strVal) return undefined;

  const numVal = +strVal;
  return Number.isInteger(numVal) ? numVal : undefined;
}
